import { Button } from "../../../components/core/basic/Button";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Center, Flex, VStack, Wrap } from "../../../../styled-system/jsx";
import { useAdPlanTags } from "../hooks/useAdPlanTags";
import { useTagsQuery } from "../../tags/api/useTagsQuery";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { Select } from "../../../components/fields/base/Select";
import { MultiSelect } from "../../../components/fields/base/MultiSelect";
import { AsinListInput } from "../../../components/fields/base/StringListInput.presets";
import { TextField } from "../../../components/fields/base/TextField";
import { BsBoxSeamFill } from "react-icons/bs";
import { BiSolidPurchaseTag, BiSolidPurchaseTagAlt } from "react-icons/bi";
import { FaHashtag } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { useGetV4ProductReportOptions } from "../../perf_reports/api/product/v4ProductReportOptions";
import { apiClient } from "../../../zodios/client";
import { useAppSelector } from "../../../redux/hooks";
import { Switch } from "../../../components/fields/base/Switch";
import { NEW_TO_BRAND_ASIN_QUERY, NEW_TO_BRAND_CAMPAIGN_QUERY } from "../utils/utils";

interface Query {
  subscribed: boolean;
  frequency: string;
  parameters: Partial<FormData>;
  parameter_list: (keyof FormData)[];
  description?: string;
  name?: string;
  query_type?: string;
}

interface FormData {
  days: number;
  frequency: string;
  tag: string[];
  subscription_title: string;
  campaignIds: string[];
  asins: string[];
  productTags: string[];
}

type FormSubmission = FormData & { name?: string };
type GroupByOption = "campaign" | "asin";

interface QueryFormProps {
  setShowModal: (val: boolean) => void;
  query: Query;
  onFormSubmit: (formData: FormSubmission) => void;
  canCreate: boolean;
  canUpdate: boolean;
}

const QUERY_LOOKBACK_PERIOD_VALUES = [7, 14, 30, 60, 90] as const;

const QUERY_FREQUENCY_VALUES = ["daily", "weekly", "monthly"] as const;

const getInitialFormData = (query?: Query): FormData => {
  if (!query) {
    return {
      days: QUERY_LOOKBACK_PERIOD_VALUES[2],
      frequency: QUERY_FREQUENCY_VALUES[1],
      tag: [],
      subscription_title: "",
      campaignIds: [],
      asins: [],
      productTags: [],
    };
  }
  return {
    days: query.subscribed ? (query.parameters.days ?? 30) : QUERY_LOOKBACK_PERIOD_VALUES[2],
    frequency: query.subscribed ? query.frequency : QUERY_FREQUENCY_VALUES[1],
    tag: query.subscribed ? (query.parameters.tag ?? []) : [],
    subscription_title: query.subscribed ? (query.parameters.subscription_title ?? "") : "",
    campaignIds: query.subscribed ? (query.parameters.campaignIds ?? []) : [],
    asins: query.subscribed ? (query.parameters.asins ?? []) : [],
    productTags: query.subscribed ? (query.parameters.productTags ?? []) : [],
  };
};

const QueryForm = ({ setShowModal, query, onFormSubmit, canCreate, canUpdate }: QueryFormProps) => {
  const { t } = useTranslation(["amc", "common"]);
  const [formData, setFormData] = useState(getInitialFormData(query));
  const queryParametersToRender = query.parameter_list;
  const { description } = query;

  const lookbackPeriodOptions = QUERY_LOOKBACK_PERIOD_VALUES.map((value) => ({
    value,
    label: t(($) => $.amc["{count} Days"], { count: value }),
  }));

  const frequencyOptions = [
    { value: "daily" as const, label: t(($) => $.amc["Daily"]) },
    { value: "weekly" as const, label: t(($) => $.amc["Weekly"]) },
    { value: "monthly" as const, label: t(($) => $.amc["Monthly"]) },
  ];
  const marketPlace = useAppSelector((s) => s.marketPlace.marketPlace);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const { data: campaignPage = [], isLoading: isCampaignsLoading } = useQuery({
    queryKey: ["channel_campaigns", marketPlace],
    queryFn: async () =>
      await apiClient.listChannelCampaigns({
        queries: {
          marketplace: marketPlace,
          state: "enabled",
          mediaplan_type: "sp,sd,sb",
        },
      }),
  });

  const { campaignSuggestions, nameToId, idToName } = useMemo(() => {
    const suggestions: string[] = [];
    const seen = new Set<string>();
    const nameToId = new Map<string, string>();
    const idToName = new Map<string, string>();

    for (const { campaignId, name } of campaignPage) {
      const cname = name.trim();
      const id = campaignId;
      if (!seen.has(cname)) {
        seen.add(cname);
        suggestions.push(cname);
      }
      nameToId.set(cname, id);
      idToName.set(id, cname);
    }

    return { campaignSuggestions: suggestions, nameToId, idToName };
  }, [campaignPage]);

  const selectedCampaignNames = formData.campaignIds.map((id) => idToName.get(id) || id);

  const needsTagOptions = query.parameter_list.includes("tag");
  const { data: legacyTagOptions, isLoading: isLegacyTagsLoading } = useAdPlanTags(
    needsTagOptions && !tagsEnhancedEnabled,
  );
  const entityTagsQuery = useTagsQuery(
    { entityType: "ADVERTISEMENT" },
    { enabled: needsTagOptions && tagsEnhancedEnabled },
  );
  const tagOptions = tagsEnhancedEnabled ? (entityTagsQuery.data?.results ?? []).map((t) => t.name) : legacyTagOptions;
  const isTagsLoading = tagsEnhancedEnabled ? entityTagsQuery.isLoading : isLegacyTagsLoading;
  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());

  const productTagOptions = (productReportOptionsQuery.data?.data.tags ?? []).map((tag: string) => ({
    value: tag,
    label: tag,
  }));

  const rawQueryName = query.query_type ?? query.name ?? "";
  const isNewToBrandQuery = rawQueryName === NEW_TO_BRAND_CAMPAIGN_QUERY || rawQueryName === NEW_TO_BRAND_ASIN_QUERY;

  const defaultGroupBy: GroupByOption = rawQueryName === NEW_TO_BRAND_ASIN_QUERY ? "asin" : "campaign";
  const [groupBy, setGroupBy] = useState<GroupByOption>(defaultGroupBy);

  const handleFormSubmit = () => {
    if (isNewToBrandQuery) {
      const nextName = groupBy === "campaign" ? NEW_TO_BRAND_CAMPAIGN_QUERY : NEW_TO_BRAND_ASIN_QUERY;
      onFormSubmit({ ...formData, name: nextName });
      setShowModal(false);
      return;
    }

    onFormSubmit(formData);
    setShowModal(false);
  };

  return (
    <>
      {description && <p className="text-center fs-standard">{description}</p>}
      <VStack
        css={{
          display: "grid",
          gap: "1rem",
          maxHeight: "70vh",
          paddingLeft: "10%",
          paddingRight: "10%",
          overflowY: "auto",
        }}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <Center>
            <TextField
              label={<Center width="100%">{t(($) => $.amc["Title"])}</Center>}
              placeholder={t(($) => $.amc["e.g. ASIN AMC Query"])}
              value={formData.subscription_title}
              onChange={(value: string) => setFormData({ ...formData, subscription_title: value })}
              css={{ borderRadius: "1px", maxWidth: "400px" }}
            />
          </Center>

          <Wrap className={css({ paddingTop: "medium" })}>
            <Select
              css={{ margin: "medium" }}
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  {t(($) => $.amc["Frequency"])}{" "}
                  <HoverInfo iconCss={{ color: "gray", fontSize: "small" }}>
                    {t(($) => $.amc["Daily: Gets a Report Every Day"])} <br />
                    {t(($) => $.amc["Weekly: Gets a Report Every Monday"])} <br />
                    {t(($) => $.amc["Monthly: Gets a Report Every First Day of the Month"])}
                  </HoverInfo>
                </span>
              }
              items={frequencyOptions}
              selectedKey={formData.frequency}
              onSelectionChange={(key) => {
                if (key) setFormData({ ...formData, frequency: key as string });
              }}
            />

            {queryParametersToRender.includes("days") && (
              <Select
                css={{ margin: "medium" }}
                label={
                  <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                    {t(($) => $.amc["Lookback Period"])}
                  </span>
                }
                items={lookbackPeriodOptions}
                selectedKey={formData.days}
                onSelectionChange={(key) => {
                  if (typeof key === "number") setFormData({ ...formData, days: key });
                }}
              />
            )}
          </Wrap>

          {isNewToBrandQuery && (
            <Flex
              className={css({ gap: "medium", marginY: "medium", fontSize: "small" })}
              justifyContent="center"
              alignItems="center"
            >
              <span className={css({ fontWeight: groupBy === "campaign" ? 600 : 400 })}>
                {t(($) => $.amc["Group by Campaign"])}
              </span>
              <Switch
                aria-label={t(($) => $.amc["Toggle grouping for New to Brand conversions"])}
                isSelected={groupBy === "asin"}
                onChange={(isSelected) => setGroupBy(isSelected ? "asin" : "campaign")}
              />
              <span className={css({ fontWeight: groupBy === "asin" ? 600 : 400 })}>
                {t(($) => $.amc["Group by ASIN"])}
              </span>
            </Flex>
          )}

          {queryParametersToRender.includes("campaignIds") && (
            <MultiSelect
              css={{
                width: "100%",
                paddingTop: "small",
                paddingBottom: "large",
              }}
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  {t(($) => $.amc["Media Plans (optional)"])}
                  <HoverInfo iconCss={{ color: "gray", fontSize: "small" }}>
                    {t(($) => $.amc["Filter to these campaigns."])}
                  </HoverInfo>
                </span>
              }
              items={Array.from(new Set([...selectedCampaignNames, ...campaignSuggestions])).map((name) => ({
                label: name,
                value: name,
              }))}
              value={selectedCampaignNames}
              onChange={(keys) => {
                const ids = keys.map((k) => nameToId.get(String(k))).filter((id): id is string => !!id);
                setFormData({ ...formData, campaignIds: ids });
              }}
              placeholder={t(($) => $.amc["Search & Select Campaigns…"])}
              isLoading={isCampaignsLoading}
              startIcon={<FaHashtag className={css({ color: "gray", fontSize: "small" })} />}
              aria-label={t(($) => $.amc["Media Plans"])}
            />
          )}

          {queryParametersToRender.includes("asins") && (
            <AsinListInput
              css={{
                width: "100%",
                paddingTop: "small",
                paddingBottom: "large",
              }}
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  {t(($) => $.amc["ASINs (optional)"])}
                  <HoverInfo iconCss={{ color: "gray", fontSize: "small" }}>
                    {t(($) => $.amc["Filter to campaigns linked to these ASINs."])}
                  </HoverInfo>
                </span>
              }
              startIcon={<BsBoxSeamFill className={css({ color: "gray", fontSize: "small" })} />}
              placeholder={t(($) => $.amc["Add ASINs here..."])}
              value={formData.asins}
              onChange={(asins) => setFormData({ ...formData, asins })}
              bulkEditTitle={t(($) => $.amc["ASINs"])}
            />
          )}
          {queryParametersToRender.includes("productTags") && (
            <MultiSelect
              css={{
                width: "100%",
                paddingTop: "small",
                paddingBottom: "large",
              }}
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  {t(($) => $.amc["Product Tags (optional)"])}
                  <HoverInfo iconCss={{ color: "gray", fontSize: "small" }}>
                    {t(($) => $.amc["Filter to campaigns linked to these product tags."])}
                  </HoverInfo>
                </span>
              }
              items={Array.from(new Set([...formData.productTags, ...productTagOptions.map((t) => t.value)])).map(
                (tag) => ({ label: tag, value: tag }),
              )}
              value={formData.productTags}
              onChange={(keys) => {
                if (!productTagOptions.length) return;
                setFormData({ ...formData, productTags: keys.map(String) });
              }}
              placeholder={t(($) => $.amc["Search & Select Product Tags…"])}
              isLoading={productReportOptionsQuery.isLoading}
              startIcon={<BiSolidPurchaseTag className={css({ color: "gray", fontSize: "small" })} />}
              aria-label={t(($) => $.amc["Product Tags"])}
            />
          )}
          {queryParametersToRender.includes("tag") && (
            <MultiSelect
              css={{
                width: "100%",
                paddingTop: "small",
                paddingBottom: "large",
              }}
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  {t(($) => $.amc["Media Plan Tags (optional)"])}
                  <HoverInfo iconCss={{ color: "gray", fontSize: "small" }}>
                    {t(($) => $.amc["Filter to campaigns with these media plan tags."])}
                  </HoverInfo>
                </span>
              }
              items={Array.from(new Set([...formData.tag, ...(tagOptions ?? [])])).map((tag) => ({
                label: tag,
                value: tag,
              }))}
              value={formData.tag}
              onChange={(keys) => setFormData({ ...formData, tag: keys.map(String) })}
              placeholder={t(($) => $.amc["Search & Select Media Plan Tags…"])}
              isLoading={isTagsLoading}
              startIcon={<BiSolidPurchaseTagAlt className={css({ color: "gray", fontSize: "small" })} />}
              aria-label={t(($) => $.amc["Media Plan Tags"])}
            />
          )}

          <Flex className={css({ padding: "large" })} gap="xlarge" justifyContent="center">
            <Button variant="secondary" onPress={() => setShowModal(false)}>
              {t(($) => $.common.actions["Cancel"])}
            </Button>
            <Button
              isDisabled={query.subscribed ? !canUpdate : !canCreate}
              onPress={() => {
                handleFormSubmit();
              }}
            >
              {query.subscribed ? t(($) => $.amc["Update"]) : t(($) => $.amc["Subscribe"])}
            </Button>
          </Flex>
        </form>
      </VStack>
    </>
  );
};

export default QueryForm;
